import type { Dictionary } from 'lodash';

import {
  renderQuery,
  parseQuery,
  assertValue,
  exhaustiveGuard,
} from '@libs/utils';
import type { QueryString } from '@libs/utils';

import type { Coordinate } from './coordinate';
import { SelectionPrefix } from './selectionPrefix';

export interface HotelSelection {
  prefix: string;
  serializeValue(): string;
}

export interface HotelConstraint {
  selection: HotelSelection;
  starRating?: number;
  carefullySelectedOnly?: boolean;
  maxNumberOfHotels?: number;
  onlyHotelsWithPool?: boolean;
  onlyHotelsWithParking?: boolean;
  onlyHotelsWithWifi?: boolean;
  onlyHotelsWithGym?: boolean;
  onlyHotelsWithAir?: boolean;
  onlyHotelsWithSpa?: boolean;
  onlyHotelsWithVehicleChargingStation?: boolean;
  onlyHotelsWithPetsAllowed?: boolean;
  onlyHotelsWithBusinessProperty?: boolean;
  onlyHotelsWithFamilyProperty?: boolean;
  onlyHotelsWithGreenSustainableProperty?: boolean;
  onlyHotelsWithFreeCancellation?: boolean;
  conceptIds?: number[];
  hotelName?: string;
}

export class ThemeConstraint implements HotelSelection {
  public readonly prefix = SelectionPrefix.Theme;

  constructor(private tagId: number) {}

  public serializeValue(): string {
    return this.tagId.toString();
  }

  public static deserializeValue(themeId: string): number {
    return parseInt(themeId, 10);
  }
}

export class DestinationConstraint implements HotelSelection {
  public readonly prefix = SelectionPrefix.Destination;

  constructor(private destination: number) {}

  public serializeValue(): string {
    return this.destination.toString();
  }

  public static deserializeValue(destination: string): number {
    return parseInt(destination, 10);
  }
}

export class PolygonConstraint implements HotelSelection {
  public readonly prefix = SelectionPrefix.Polygon;

  constructor(public coordinates: Coordinate[]) {}

  public serializeValue(): string {
    const coordinates: number[][] = [];

    for (const coordinate of this.coordinates) {
      coordinates.push([coordinate.longitude, coordinate.latitude]);
    }

    const searchTextObject = {
      type: 'polygon',
      coordinates: [coordinates],
    };

    const stringifiedSearchTextObject = JSON.stringify(searchTextObject);
    return stringifiedSearchTextObject;
  }

  public static deserializeValue(value: string): Coordinate[] {
    const parsed = JSON.parse(value) as {
      type: string;
      coordinates: number[][][];
    };

    assertValue(
      parsed.coordinates[0],
      'hotelConstraint.ts > deserializeValue > parsed.coordinates[0]'
    );

    return parsed.coordinates[0].map((value: number[]) => {
      assertValue(value[0], 'hotelConstraint.ts > deserializeValue > value[0]');
      assertValue(value[1], 'hotelConstraint.ts > deserializeValue > value[1]');

      return {
        longitude: value[0],
        latitude: value[1],
      };
    });
  }
}

export class PolygonIdConstraint implements HotelSelection {
  public readonly prefix = SelectionPrefix.PolygonId;

  constructor(public polygonId: number) {}

  public serializeValue(): string {
    return this.polygonId.toString();
  }

  public static deserializeValue(polygonId: string): number {
    return parseInt(polygonId, 10);
  }
}

export class HotelCodeConstraint implements HotelSelection {
  public readonly prefix = SelectionPrefix.HotelCode;
  public readonly hotelCodes: string[];

  constructor(...hotelCodes: string[]) {
    this.hotelCodes = hotelCodes;
  }

  public serializeValue(): string {
    return this.hotelCodes.join(',');
  }
}

export class FullTextConstraint implements HotelSelection {
  public readonly prefix = SelectionPrefix.FullText;

  constructor(
    public freeText: string,
    public perfectMatchOnly: boolean = true
  ) {}

  public serializeValue(): string {
    return this.freeText;
  }
}

export class AllHotels implements HotelSelection {
  public readonly prefix = SelectionPrefix.AllHotels;

  public serializeValue(): string {
    return '';
  }
}

export const HotelSelectionSerializer = {
  serialize: (selection: HotelSelection): string => {
    let result = selection.prefix;

    if (result.length > 0) {
      result += ':';
    }

    result += selection.serializeValue();

    return result;
  },
  deserialize: (selectionAsString: string): HotelSelection => {
    const indexOfSeparator = selectionAsString.indexOf(':');

    if (indexOfSeparator === -1) {
      return new FullTextConstraint(selectionAsString);
    }

    const key = selectionAsString.substring(
      0,
      indexOfSeparator
    ) as SelectionPrefix;
    const value = selectionAsString.substring(indexOfSeparator + 1);

    switch (key) {
      case SelectionPrefix.PolygonId:
        return new PolygonIdConstraint(
          PolygonIdConstraint.deserializeValue(value)
        );
      case SelectionPrefix.HotelCode:
        return new HotelCodeConstraint(...value.split(','));
      case SelectionPrefix.AllHotels:
        return new AllHotels();
      case SelectionPrefix.Polygon:
        return new PolygonConstraint(PolygonConstraint.deserializeValue(value));
      case SelectionPrefix.Destination:
        return new DestinationConstraint(
          DestinationConstraint.deserializeValue(value)
        );
      case SelectionPrefix.Theme:
        return new ThemeConstraint(ThemeConstraint.deserializeValue(value));
      case SelectionPrefix.FullText:
        return new FullTextConstraint(selectionAsString);
      default:
        exhaustiveGuard(key, 'Invalid constraint selection prefix');
    }
  },
};

export function serializeConstraint(constraint: HotelConstraint): string {
  let result = HotelSelectionSerializer.serialize(constraint.selection);

  const query: QueryString = {};

  if (constraint.starRating && constraint.starRating > 1) {
    query.stars = [constraint.starRating.toString()];
  }

  if (constraint.carefullySelectedOnly) {
    query.iscarefullyselected = ['true'];
  }

  if (constraint.maxNumberOfHotels) {
    query.max = [constraint.maxNumberOfHotels.toString()];
  }

  if (constraint.onlyHotelsWithPool) {
    query.onlyhotelswithpool = ['true'];
  }

  if (constraint.onlyHotelsWithParking) {
    query.hasparking = ['true'];
  }

  if (constraint.onlyHotelsWithPetsAllowed) {
    query.petsallowed = ['true'];
  }

  if (constraint.onlyHotelsWithFreeCancellation) {
    query.freeCancellation = ['true'];
  }

  if (constraint.conceptIds) {
    query.tag = constraint.conceptIds.map((id) => id.toString());
  }

  if (Object.keys(query).length > 0) {
    result += ` (${renderQuery(query)})`;
  }

  return result;
}

export const constraintFilterValueUpdaterByFilterType: Dictionary<
  (constraint: HotelConstraint, values: string[]) => unknown
> = {
  stars: (constraint, values) => {
    assertValue(
      values[0],
      'hotelConstraint.ts > constraintFilterValueUpdaterByFilterType > stars > values[0]'
    );
    constraint.starRating = parseInt(values[0], 10);
  },
  iscarefullyselected: (constraint, values) =>
    (constraint.carefullySelectedOnly = values[0] === 'true'),
  max: (constraint, values) => {
    assertValue(
      values[0],
      'hotelConstraint.ts > constraintFilterValueUpdaterByFilterType > max > values[0]'
    );
    return (constraint.maxNumberOfHotels = parseInt(values[0], 10));
  },
  hasparking: (constraint, values) =>
    (constraint.onlyHotelsWithParking = values[0] === 'true'),
  onlyhotelswithpool: (constraint, values) =>
    (constraint.onlyHotelsWithPool = values[0] === 'true'),
  petsallowed: (constraint, values) =>
    (constraint.onlyHotelsWithPetsAllowed = values[0] === 'true'),
  freeCancellation: (constraint, values) =>
    (constraint.onlyHotelsWithFreeCancellation = values[0] === 'true'),
  tag: (constraint, values) =>
    (constraint.conceptIds = values.map((value) => parseInt(value, 10))),
};

export const filterTypes = Object.keys(
  constraintFilterValueUpdaterByFilterType
);

export const deserializeConstraint = (query: string): HotelConstraint => {
  const constraint = {} as HotelConstraint;

  let selectionAsString = query;

  if (thereAreFilters(query)) {
    const indexOfFirstFilterDelimiter = query.lastIndexOf('(');
    const indexOfLastFilterDelimiter = query.lastIndexOf(')');

    selectionAsString = getSelectionAsStringWithNoFilters(
      query,
      indexOfFirstFilterDelimiter,
      indexOfLastFilterDelimiter
    );

    const queryFilters = query.substring(
      indexOfFirstFilterDelimiter + 1,
      indexOfLastFilterDelimiter
    );
    const parsedFilters = parseQuery(queryFilters);

    Object.entries(parsedFilters).forEach(([key, values]) => {
      const value = constraintFilterValueUpdaterByFilterType[key.toLowerCase()];
      assertValue(value, 'hotelConstraint.ts > deserializeConstraint > value');
      return value(constraint, values);
    });
  }
  constraint.selection =
    HotelSelectionSerializer.deserialize(selectionAsString);

  return constraint;
};

const thereAreFilters = (query: string): boolean => {
  const indexOfFirstFilterDelimiter = query.lastIndexOf('(');
  return (
    indexOfFirstFilterDelimiter !== -1 &&
    filterTypes.some((filter) =>
      query
        .substring(indexOfFirstFilterDelimiter)
        .toLowerCase()
        .includes(filter)
    )
  );
};

const getSelectionAsStringWithNoFilters = (
  query: string,
  filterFirstPosition: number,
  filterLastPosition: number
): string => {
  if (filtersAreAtTheEndOfQuery(query, filterLastPosition)) {
    return query.substring(0, filterFirstPosition).trim();
  }

  const prefix = query.substring(0, filterFirstPosition);
  const value = query.substring(filterLastPosition + 1);

  return `${prefix}${value}`;
};

const filtersAreAtTheEndOfQuery = (
  query: string,
  filterLastPosition: number
): boolean => query.length === filterLastPosition + 1;
